<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { Button as KButton } from '@progress/kendo-vue-buttons'
import {
  ListView,
  ListViewHeader,
} from '@progress/kendo-vue-listview';
import { useLogger } from 'vue-logger-plugin'
import { ref, watch, computed } from 'vue'
import { setStoreLogger } from '@/logger.ts'
import AoeConsultantLocatorDetail from './AoeConsultantLocatorDetail.vue'
import AoeHostDetail from './AoeHostDetail.vue'
import type { IEventHost } from '@/types/EventTypes'
import { EventAddToCart, PayeeAddToCart } from '@/services/CartService';
import { GetConsultantFromLocalStorageOrApi } from '@/services/ConsultantService'
import type { IConsultantLocator } from '@/types/LocatorTypes'
import { GetLocatorConsultants, GetLocatorConsultantsByZip,GetLocatorConsultantsKnowAll } from '@/services/LocatorService'
import {
  GetCountryCode, GetHostBenefitsUrl
} from '@/stores/SettingsStore.ts'
import { AutoComplete, type AutoCompleteChangeEvent, DropDownList } from '@progress/kendo-vue-dropdowns'
import { TextBox as KTextBox, RadioButton } from '@progress/kendo-vue-inputs'
import type { NumericDropdownOption } from '@/types/DropdownTypes'
import type { DropdownOption } from '@/types/DropdownTypes'
import type {
  IAoeCart
} from '@/types/CartTypes'
import { SetIsLoading } from '@/services/AoeHelpers.ts'
import { searchIcon } from '@progress/kendo-svg-icons'
import { useCookies } from 'vue3-cookies'
import { formatConsultantLocation } from '@/utils/locationHelper'

const logger = useLogger()
setStoreLogger(logger)
logger.debug('AOECartDrawer.vue')

const countryCode = ref<string>('')
const selectedCountry = ref<string>('')
const locatorConsultants = ref<IConsultantLocator[]>([])
const mapLocatorConsultants = ref<IConsultantLocator[]>([])
const selectedEventId = ref<number>(0)
const selectedConsultant = ref<number>(0)
const consultantByName = ref(true)
const consultantByLocation = ref(false)
const txtZipCode = ref<string>('')
const hostBenefitsUrl = ref<string>('')
const hasSearched = ref(false)
const searchRadius = ref<number>(25)
const { cookies } = useCookies()
const filteredEventHosts = ref<IEventHost[]>([])
const selectedConsultantText = ref<string>('')
const searchText = ref<string>('')

const radiusOptions: NumericDropdownOption[] = [
  { value: 5 },
  { value: 25 },
  { value: 50 },
  { value: 100 }
]

const countryOptions: DropdownOption[] = [
  { value: 'DE', labelKey: 'countries.germany' },
  { value: 'AT', labelKey: 'countries.austria' },
  { value: 'FR', labelKey: 'countries.france' }
]

const countryOptionsWithLabels = computed(() => {
  return countryOptions.map((opt: DropdownOption) => ({
    value: opt.value,
    label: t(opt.labelKey)
  }))
})

const props = defineProps<{
  cart: IAoeCart | null
  ispopover: boolean
  isPartyOnly: boolean
  eventHosts: IEventHost[]
}>()

const emit = defineEmits(["closePopup", "consultantSelected"]);


function selectedHostHandler(event: IEventHost)
{
  logger.debug('selectedHostHandler', event)
  selectedEventId.value = event.eventId
}

function selectedConsultantHandler(event: IConsultantLocator)
{
  logger.debug('selectedConsultantHandler', event)
  selectedConsultant.value = event.beeNumber
  selectedConsultantText.value = event.name
  logger.debug('Consultant selected', { beeNumber: event.beeNumber, name: event.name })
}

function filterHostsHandler(event: AutoCompleteChangeEvent)
{
  // Handle clear button click
  if (event.event != undefined && event.event.currentTarget.className === "k-clear-value")
  {
    logger.debug('clearHostTextHandler')
    selectedEventId.value = 0
    filteredEventHosts.value = []
    return
  }

  const filterValue = event.target.value?.toLowerCase() || ''
  logger.debug('filterHostsHandler', filterValue)

  if (filterValue.length === 0) {
    filteredEventHosts.value = []
  } else {
    filteredEventHosts.value = props.eventHosts.filter(host =>
      host.hostName?.toLowerCase().includes(filterValue) ||
      host.eventId?.toString().includes(filterValue)
    )
  }
}

GetCountryCode().then(cc =>
{
  countryCode.value = cc
  selectedCountry.value = cc
})

GetHostBenefitsUrl().then(url =>
{
  hostBenefitsUrl.value = url
})

const latestConsultantSearchRequestId = ref(0)

async function performConsultantSearch(searchValue: string)
{
  // Small delay to allow selection event to fire first if user clicked from dropdown
  await new Promise(resolve => setTimeout(resolve, 100))

  // Check again if consultant was selected during the delay
  if (selectedConsultantText.value && searchValue === selectedConsultantText.value) {
    logger.debug('Consultant selected during delay, skipping search')
    return
  }

  const requestId = ++latestConsultantSearchRequestId.value

  SetIsLoading(true)
  try {
    // Check if search value contains a space
    if (searchValue.indexOf(' ') > 0) {
      // Split by space - first part is firstName, second part is lastName
      const names = searchValue.split(' ')
      const firstName = names[0]
      const lastName = names.slice(1).join(' ') // Handle multiple spaces/names

      logger.debug('Searching consultants by full name', { firstName, lastName })

      // Make a single API call with both firstName and lastName
      const response = await GetLocatorConsultantsKnowAll({
        firstName: firstName,
        lastName: lastName,
        includePublicStoreDisabled: false
      })

      logger.debug('GetLocatorConsultants response', { response })

      if (requestId !== latestConsultantSearchRequestId.value) {
        logger.debug('Ignoring stale consultant search response', requestId)
        return
      }

      // Add aoeCartIdToken to each consultant and replace (never accumulate) the list
      const consultants = response?.items || []
      locatorConsultants.value = consultants.map(consultant => {
        const eH = consultant as IConsultantLocator
        eH.aoeCartIdToken = props.cart?.aoeCartIdToken || ''
        return eH
      })
    } else {
      // No space - search by first name OR last name
      logger.debug('Searching consultants by first name and last name', { searchValue })

      // Make two parallel API calls - one for first name, one for last name
      const [firstNameResponse, lastNameResponse] = await Promise.all([
        GetLocatorConsultantsKnowAll({
          firstName: searchValue,
          lastName: null,
          includePublicStoreDisabled: false
        }),
        GetLocatorConsultantsKnowAll({
          firstName: null,
          lastName: searchValue,
          includePublicStoreDisabled: false
        })
      ])

      logger.debug('GetLocatorConsultants responses', { firstNameResponse, lastNameResponse })

      if (requestId !== latestConsultantSearchRequestId.value) {
        logger.debug('Ignoring stale consultant search response', requestId)
        return
      }

      // Combine results from both searches
      const firstNameResults = firstNameResponse?.items || []
      const lastNameResults = lastNameResponse?.items || []

      // Create a Map to deduplicate by beeNumber
      const consultantMap = new Map<number, IConsultantLocator>()

      // Add all first name results
      firstNameResults.forEach(consultant => {
        const eH = consultant as IConsultantLocator
        eH.aoeCartIdToken = props.cart?.aoeCartIdToken || ''
        consultantMap.set(consultant.beeNumber, eH)
      })

      // Add all last name results (will not duplicate if beeNumber already exists)
      lastNameResults.forEach(consultant => {
        const eH = consultant as IConsultantLocator
        eH.aoeCartIdToken = props.cart?.aoeCartIdToken || ''
        consultantMap.set(consultant.beeNumber, eH)
      })

      // Convert Map back to array - replaces (never accumulates) the list
      locatorConsultants.value = Array.from(consultantMap.values())

      logger.debug('Combined and deduplicated consultants', {
        firstNameCount: firstNameResults.length,
        lastNameCount: lastNameResults.length,
        totalUnique: locatorConsultants.value.length,
        consultants: locatorConsultants.value
      })
    }
  } catch (error) {
    console.error('Error fetching consultants:', error)
    if (requestId === latestConsultantSearchRequestId.value) {
      locatorConsultants.value = []
    }
  } finally {
    if (requestId === latestConsultantSearchRequestId.value) {
      SetIsLoading(false)
    }
  }
}

const debouncedPerformConsultantSearch = debounce((searchValue: string) => {
  void performConsultantSearch(searchValue)
}, 300)

function changeConsultantTextHandler(event: AutoCompleteChangeEvent)
{
  consultantByName.value = true

  if (event.event != undefined && event.event.currentTarget.className === "k-clear-value")
  {
    logger.debug('clearConsultantTextHandler')
    selectedConsultant.value = 0
    selectedConsultantText.value = ''
    locatorConsultants.value = []
    searchText.value = ''
    latestConsultantSearchRequestId.value += 1
  }
  else
  {
    searchText.value = event.target.value || ''

    const searchValue = event.target.value

    // Check if this is a selection from the dropdown (value matches a consultant name)
    // If so, don't trigger a new search
    if (selectedConsultantText.value && searchValue === selectedConsultantText.value) {
      logger.debug('Consultant already selected, skipping search', { searchValue })
      return
    }

    if (searchValue.length >= 3)
    {
      locatorConsultants.value = []
      debouncedPerformConsultantSearch(searchValue)
    } else {
      latestConsultantSearchRequestId.value += 1
      locatorConsultants.value = []
    }
  }
}

function radioBtnChangeNameHandler()
{
  logger.debug('radioBtnChangeNameHandler')
  consultantByName.value = true
  consultantByLocation.value = false
}

function radioBtnChangeLocationHandler()
{
  logger.debug('radioBtnChangeLocationHandler')
  consultantByName.value = false
  consultantByLocation.value = true
}

async function btnFindPartyConfirmHandler()
{
  logger.debug('btnFindPartyConfirmHandler')

  emit('closePopup')

  if (selectedEventId.value > 0)
  {
    await EventAddToCart({
        aoeCartIdToken: props.cart?.aoeCartIdToken || '',
        eventId: selectedEventId.value
    })
  }
  else if (selectedConsultant.value > 0)
  {
    await PayeeAddToCart({
        aoeCartIdToken: props.cart?.aoeCartIdToken || '',
        beeNumberPayee: selectedConsultant.value
    }).then(async () => {
      window.location.href = window.location.href
      cookies.set('ReplicatedSiteBeeNumber', selectedConsultant.value.toString());
    })
  }
}

function resetForm() {
  selectedConsultant.value = 0
  selectedEventId.value = 0
  selectedConsultantText.value = ''
  locatorConsultants.value = []
  mapLocatorConsultants.value = []
  txtZipCode.value = ''
  hasSearched.value = false
  consultantByName.value = true
  consultantByLocation.value = false
  filteredEventHosts.value = []
  searchText.value = ''
}

function closeCheckoutPopupHandler()
{
  logger.debug('closeCheckoutPopupHandler')
  resetForm()
  emit('closePopup')
}

watch(txtZipCode, (newVal) => {
  if (!newVal || newVal.trim().length === 0) {
    mapLocatorConsultants.value = []
    hasSearched.value = false
  }
})

function handleRadiusChange(e: any) {
  const newValue = e.value?.value !== undefined ? e.value.value : e.value
  searchRadius.value = Number(newValue)
  if (txtZipCode.value) {
    searchClickHandler()
  }
}

async function searchClickHandler() {
  logger.debug('searchClickHandler')

  SetIsLoading(true)
  hasSearched.value = false
  mapLocatorConsultants.value = []

  const zip = txtZipCode.value

  GetLocatorConsultantsByZip({
    postalCode: zip,
    distance: searchRadius.value,
    countryCode: selectedCountry.value,
    includePublicStoreDisabled: false
  }).then((locConsultantResp) => {
    for (const locCon of locConsultantResp.items) {
      const eH = locCon as IConsultantLocator
      eH.aoeCartIdToken = props.cart?.aoeCartIdToken || ''
      mapLocatorConsultants.value.push(eH)
    }

    hasSearched.value = true
    logger.debug('mapLocatorConsultants', mapLocatorConsultants)
  }).finally(() => {
    SetIsLoading(false)
  })
}

// Simple debounce utility for a single-string-argument function
function debounce(func: (value: string) => void, wait: number) {
  let timeout: ReturnType<typeof setTimeout> | null = null
  return function (value: string) {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(value), wait)
  }
}
</script>

<template>
  <div class="row mt-3 cart-popup-event-locator">
        <div v-if="!isPartyOnly" class="col-11 popOverGuest">
          <p class="pageHeader">{{ t('cart.FindConsultatHeader') }}</p>
          <p class="pageSubSubHeader">{{ t('cart.FindConsultatSubHeader') }}</p>
          <div class="row cart-popup-event-locator-search-options">
            <div class="col-6">
              <RadioButton
                :name="'group1'"
                size="large"
                :checked="consultantByName"
                @change="radioBtnChangeNameHandler"
                >{{ t('cart.ConsultantByName') }}
              </RadioButton>
            </div>
            <div class="col-6">
              <RadioButton
                :name="'group1'"
                size="large"
                :checked="consultantByLocation"
                @change="radioBtnChangeLocationHandler"
                >{{ t('cart.ConsultantByLocation') }}
              </RadioButton>
            </div>
          </div>
          <div v-if="consultantByName" class="row mt-3">
            <div class="col-12">
              <AutoComplete class="consultantAutoComplete"
                :data-items="locatorConsultants"
                :placeholder="t('cart.PlaceHolderConsultantName')"
                :text-field="'name'"
                :value="searchText"
                :item-render="AoeConsultantLocatorDetail"
                @selected-consultant="selectedConsultantHandler"
                @change="changeConsultantTextHandler"
              >
                <template v-slot:myTemplate="{ props }">
                  <custom
                    :style="props.style"
                    :class="props.itemClass"
                    :data-item="props.dataItem"
                    :focused="props.focused"
                    :index="props.index"
                    />
                </template>
              </AutoComplete>
            </div>
          </div>
          <div v-if="consultantByLocation" class="row mt-3">
            <div class="col-12">
              <div class="locator-search-bar">
                <DropDownList
                  :data-items="countryOptionsWithLabels"
                  :text-field="'label'"
                  :data-item-key="'value'"
                  :value="countryOptionsWithLabels.find((opt: any) => opt.value === selectedCountry)"
                  @change="(e: any) => selectedCountry = e.value.value"
                  class="country-dropdown"
                />
                <KTextBox
                    :placeholder="t('cart.PlaceholderZipcode')"
                    v-model="txtZipCode"
                    :rounded="'large'"
                    :theme-color="'primary'"
                    :show-clear-button="true"
                    @keyup.enter="searchClickHandler"
                  ></KTextBox>
                <KButton
                  class="locator-search-button"
                  :svg-icon="searchIcon"
                  :fill-mode="'clear'"
                  @click="searchClickHandler"
                ></KButton>
              </div>
              <div class="radius-selector-container">
                <label class="radius-label">{{ t('consultantLocator.searchRadius') }}</label>
                <DropDownList
                  :data-items="radiusOptions"
                  :text-field="'value'"
                  :data-item-key="'value'"
                  :value="radiusOptions.find(opt => opt.value === searchRadius)"
                  @change="handleRadiusChange"
                  class="radius-dropdown"
                />
                <span class="radius-unit">{{ t('consultantLocator.kilometers') }}</span>
              </div>
              <div v-if="hasSearched && mapLocatorConsultants.length === 0" class="row mt-3">
                <div class="col-12">
                  <div class="no-results-message">
                    {{ t('consultantLocator.noConsultantsFound') }}
                  </div>
                </div>
              </div>
              <div v-if="mapLocatorConsultants.length > 0" class="row mt-3">
                <div class="col-12 map-locator-results">
                  <ListView
                    :style="{ width: '100%', height: '300px' }"
                    :data-items="mapLocatorConsultants"
                    :item="'myItem'"
                    :header="'myHeader'"
                  >
                    <template v-slot:myItem="{ props }">
                      <div class="locatorItem mt-2" :class="props.dataItem.beeNumber == selectedConsultant ? 'selectedConsultant' : ''">
                        <div class="locatorItemCell">
                            <img v-if="props.dataItem.photoUrl && props.dataItem.showPhoto !== false"
                              :src="props.dataItem.photoUrl"
                              :alt="t('cart.ConsultantPhotoAlt')"
                              class="locatorPhoto">
                        </div>
                        <div class="locatorItemCell">
                          <div class="row" v-if="props.dataItem.showName !== false">
                            <div class="col-12 font-weight-bold">
                                  {{ props.dataItem.name }}
                            </div>
                          </div>
                          <div class="row" v-if="props.dataItem.showLocation !== false">
                            <div class="col-12">
                                  {{ formatConsultantLocation(props.dataItem) }}
                            </div>
                          </div>
                          <div class="row" v-if="props.dataItem.emailAddress && props.dataItem.showEmail !== false">
                            <div class="col-12">
                              ✉ {{ props.dataItem.emailAddress }}
                            </div>
                          </div>
                          <div class="row" v-if="(props.dataItem.mobilePhone && props.dataItem.showMobile !== false) || (props.dataItem.landline && props.dataItem.showLandline !== false)">
                            <div class="col-12">
                              ☎
                              <span v-if="props.dataItem.mobilePhone && props.dataItem.showMobile !== false" class="mr-2">{{ props.dataItem.mobilePhone }} (M)</span>
                              <span v-if="props.dataItem.landline && props.dataItem.showLandline !== false">{{ props.dataItem.landline }} (L)</span>
                            </div>
                          </div>
                          <div class="locatorItemButtonWrap">
                            <KButton
                              class="btnLocatorSelect"
                              :rounded="'large'"
                              :theme-color="'btnLocatorSelect'"
                              @click="selectedConsultantHandler(props.dataItem)"
                            >{{ t('cart.SelectButton') }}</KButton>
                          </div>
                        </div>
                        <div class="locatorItemCellButton text-center">
                          <KButton
                          class="btnLocatorSelect"
                          :rounded="'large'"
                          :theme-color="'btnLocatorSelect'"
                            @click="selectedConsultantHandler(props.dataItem)"
                            >{{ t('cart.SelectButton') }}</KButton>
                        </div>
                      </div>
                    </template>
                    <template v-slot:myHeader>
                      <ListViewHeader
                        :style="{ color: '#a0a0a0' }"
                        class="pl-3 pb-2 pt-2 text-small"
                      >
                        {{ t('cart.ConsultantListHeader') }}
                      </ListViewHeader>
                    </template>
                  </ListView>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-1 closePopup">
          <KButton
            :fill-mode="'clear'"
            @click="closeCheckoutPopupHandler"
            class="popupCloseButton"
            >&#x2715;</KButton
          >
        </div>
      </div>
      <div class="row mt-3 locator-action-row">
        <div class="col-6 locator-action-spacer"></div>
        <div class="col-3 btnFindPartyCancelDiv">
          <KButton
            id="btnFindPartyCancel"
            ref="btnFindPartyCancel"
            class="btnFindPartyCancel"
            :rounded="'large'"
            :theme-color="'btnFindPartyCancel'"
            @click="closeCheckoutPopupHandler"
            >{{ t('labels.Cancel') }}</KButton>
        </div>
        <div class="col-3 btnFindPartyConfirmDiv">
          <KButton
            id="btnFindPartyConfirm"
            ref="btnFindPartyConfirm"
            class="btnFindPartyConfirm"
            :rounded="'large'"
            :theme-color="'primary'"
            :disabled="selectedConsultant === 0 && selectedEventId === 0"
            @click="btnFindPartyConfirmHandler()"
            >{{ t('labels.Confirm') }}</KButton>
        </div>
      </div>
</template>

<style lang="scss">

.cart-popup-event-locator {
  .cart-popup-event-locator-search-options {
    margin-bottom: $spacing-2;

    .k-radio {
      margin-right: $spacing-2;
    }
  }

  .locator-search-bar {
    display: flex;
    align-items: center;
    gap: $spacing-2;

    .k-textbox {
      flex: 1;
    }

    .country-dropdown {
      width: 120px;
      min-width: 120px;
    }
  }

  .locatorItemButtonWrap {
    display: none;
  }
}

.hostAutoComplete input::placeholder,
.consultantAutoComplete input::placeholder {
  @include typography-small;
}

.locatorItem {
  display: grid;
  grid-template-columns: 25% 45% 25%;
  padding: $spacing-2-5;
}

.locatorItem > div {
  padding: $spacing-1;
}

.locatorItemCellButton {
  display: flex;
  justify-content: center;
  align-items: center;
}

@media (max-width: 991px) {
  .cart-popup-event-locator {
    .locatorItem {
      grid-template-columns: 1fr 2fr;
      gap: $spacing-2-5;
    }

    .locatorItemButtonWrap {
      display: block;
      margin-top: $spacing-2;
    }

    .locatorItemCellButton {
      display: none;
    }
  }
}

.btnLocatorSelect
{
  background-color: #b0d3dc;
}

.no-results-message {
  text-align: center;
  padding: 1.5rem;
  margin: 1rem 0;
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  @include typography-body;
  border-radius: $radius-md;
  color: #856404;
}
.selectedConsultant
{
  background-color: #b0d3dc;
}

.locatorPhoto
{
  width: 75%;
}

.locator-search-button {
  /* !important required: Override Kendo button defaults for larger search icon sizing */
  padding: $spacing-2 !important;
  flex-shrink: 0;

  .k-icon,
  .k-svg-icon {
    width: 24px;
    height: 24px;
  }
}

.btnFindPartyConfirm:disabled,
.btnFindPartyConfirm[disabled] {
  opacity: 0.5;
  cursor: not-allowed;
}

.radius-selector-container {
  display: flex;
  align-items: center;
  gap: $spacing-2;
  margin-top: $spacing-4;
  margin-bottom: $spacing-2;
}

.radius-label {
  @include typography-body;
  font-weight: 500;
  color: #333;
  margin: 0;
  white-space: nowrap;
}

.radius-dropdown {
  width: 80px;
  min-width: 80px;
}

.radius-unit {
  @include typography-body;
  color: #666;
  margin-left: $spacing-1;
}

@media (max-width: 575px) {
  .locator-action-spacer {
    display: none;
  }

  .locator-action-row {
    .btnFindPartyCancelDiv,
    .btnFindPartyConfirmDiv {
      flex: 0 0 50%;
      max-width: 50%;
    }
  }

  .btnFindPartyCancel,
  .btnFindPartyConfirm {
    width: 100%;
  }
}

@media (max-width: 768px) {
  .map-locator-results {
    padding: 0;

    .locatorItemCell {
      .col-12 {
        padding: 0;
      }
    }
  }
}

</style>
